import { useCallback, useContext, useState } from 'react';

import { Formik, FormikErrors } from 'formik';
import { useDebouncedCallback } from 'use-debounce';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';
import * as Yup from 'yup';

import {
  Box,
  Button,
  Grid,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import { SubmitButton } from '../../../components/buttons';
import { ConfirmDialog } from '../../../components/dialogs';
import { FormContainer } from '../../../components/forms';
import { BrowseUpload, SearchField } from '../../../components/inputs';
import { Modal } from '../../../components/Modal';
import { Pagination } from '../../../components/paginations';
import { AlertSnackbarContext } from '../../../components/snackbars';
import { Spinner } from '../../../components/spinners';
import { Defaults, ErrorMessages } from '../../../constants';
import { useDisapproveCategory, useUpdateCategory } from '../../../lib/category/hooks';
import useGetSuggestCategory from '../../../lib/category/hooks/useGetSuggestedCategory';
import useMediaUpload from '../../../lib/media/hooks/useMediaUpload';
import { checkFileSize, SortDirection } from '../../../lib/util/helpers';
import { sortToObj } from '../../../lib/util/sortToObj';
import { Category } from '../../../types/Category';
import { Media } from '../../../types/Media';

type ApproveCategoryFormType = {
  media?: any;
};

const ApproveCategorySchema = Yup.object().shape({
  media: Yup.mixed().required(ErrorMessages.REQUIRED_PHOTO),
});

function SuggestedCategoriesList() {
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    search: StringParam,
    sort: StringParam,
  });
  const { page, search, sort } = query;

  const searchString = search && { search };
  const sortObj = sortToObj(sort ?? '');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [photosErrorMsg, setPhotosErrorMsg] = useState<string>('');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [photoMedia, setPhotoMedia] = useState<Media | null>(null);
  const debouncedSearchFn = useDebouncedCallback((value: string) => {
    setQuery({ search: value, page: 0 });
  }, Defaults.SEARCH_DEBOUNCE_MS);

  const [, setAlertSnackbar] = useContext(AlertSnackbarContext);
  const setNewPage = (page: number) => setQuery({ page: page - 1 });
  const setNewSort = (column: string, direction: SortDirection) => {
    const newSort = direction ? `${column} ${direction}` : undefined;
    setQuery({ sort: newSort });
  };

  const { upload, isUploading } = useMediaUpload({
    onSuccess: (data) => {
      setPhotoMedia(data);
    },
    onError: () => {
      setAlertSnackbar({
        message: ErrorMessages.SERVER_ERROR,
        severity: 'error',
      });
      setPhotosErrorMsg(ErrorMessages.SERVER_ERROR);
    },
  });

  const {
    data: suggestedCategories,
    isLoading,
    error,
  } = useGetSuggestCategory({
    page: Number(page) || 0,
    sort: String(sort || ''),
    ...searchString,
  });

  const { mutateAsync: updateCategory, isLoading: updateCategoryIsLoading } = useUpdateCategory();
  const disapproveSuggestedCategory = useDisapproveCategory();

  const handlePhotoUpload = useCallback(
    async (
      event: React.ChangeEvent<HTMLInputElement>,
      setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<ApproveCategoryFormType>>,
    ) => {
      const files = event.target.files;

      if (files && files[0]) {
        const file = files[0];
        try {
          checkFileSize(file);
          setPhotosErrorMsg('');

          // Upload photo to media table and S3
          await upload(files[0]);
        } catch (error) {
          let errorMessage;
          if (error instanceof Error) errorMessage = error.message;
          else errorMessage = ErrorMessages.SERVER_ERROR;

          setPhotosErrorMsg(errorMessage);
          setAlertSnackbar({
            message: errorMessage,
            severity: 'error',
          });
        }

        setFieldValue('media', file);
      }
    },
    [setAlertSnackbar, upload],
  );

  const handleApproveCategory = useCallback(async () => {
    await updateCategory(
      {
        categoryId: selectedCategoryId || '',
        params: {
          media: photoMedia,
          published: true,
        },
      },
      {
        onSuccess: () => {
          setModalIsOpen(false);
          setPhotosErrorMsg('');
          setPhotoMedia(null);
        },
      },
    );
  }, [photoMedia, selectedCategoryId, updateCategory]);

  const initialValues: ApproveCategoryFormType = { media: null };

  return (
    <>
      <Container>
        <FullWidthBar>
          <Typography variant="h5" sx={{ marginRight: '20px' }}>
            Suggested Categories
          </Typography>
          <StyledSearchField
            searchString={search ?? ''}
            onSearch={(search: string) => {
              debouncedSearchFn(search);
            }}
          />
        </FullWidthBar>

        <TableContainer sx={{ backgroundColor: 'white' }}>
          <Table aria-labelledby="tableTitle">
            <TableHead>
              <PointerTableRow>
                {columns.map((column) => (
                  <TableCell key={column.name}>
                    <TableSortLabel
                      active={sortObj.column === column.name}
                      direction={sortObj.direction}
                      onClick={() => {
                        const isAsc = sortObj.column === column.name && sortObj.direction === 'asc';
                        setNewSort(column.name, isAsc ? 'desc' : 'asc');
                      }}
                      component="a"
                    >
                      {column.text}
                      {sortObj.column === column.name ? (
                        <Box component="span" sx={visuallyHidden}>
                          {sortObj.direction === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </PointerTableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Spinner />
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                !error &&
                suggestedCategories.data &&
                suggestedCategories.data.map((row: Category, index: number) => {
                  return (
                    <StyledTableRow key={index} className={row.deleted ? 'deleted-row' : 'row'}>
                      <TableCell>{row.title}</TableCell>
                      <TableCell>{row.user?.username}</TableCell>
                      <TableCell>{new Date(row.created).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <ConfirmDialog
                          trigger={(open) => (
                            <Button onClick={open} type="button" style={{ textTransform: 'capitalize' }}>
                              Approve
                            </Button>
                          )}
                          title="Approve suggested category"
                          description="Are you sure you want to approve this category?"
                          cancelBtnText={'Cancel'}
                          submitBtnText={'Approve'}
                          onConfirm={async (close) => {
                            setSelectedCategoryId(row.id);
                            setModalIsOpen(true);
                            close();
                          }}
                        />

                        <ConfirmDialog
                          trigger={(open) => (
                            <Button onClick={open} type="button" style={{ textTransform: 'capitalize' }}>
                              Disapprove
                            </Button>
                          )}
                          title="Disapprove suggested category"
                          description="Are you sure you want to disapprove this category?"
                          cancelBtnText={'Cancel'}
                          submitBtnText={'Submit'}
                          onConfirm={async (close) => {
                            await disapproveSuggestedCategory.mutate({ categoryId: row.id });
                            close();
                          }}
                          disabled={disapproveSuggestedCategory.isLoading}
                          loading={disapproveSuggestedCategory.isLoading}
                        />
                      </TableCell>
                    </StyledTableRow>
                  );
                })}
              {suggestedCategories.data.length === 0 && (
                <StyledTableRow>
                  <TableCell colSpan={5} style={{ textAlign: 'center' }}>
                    No suggested categories records found.
                  </TableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {suggestedCategories.total !== null && suggestedCategories.total !== 0 && (
          <FullWidthBar>
            <Typography variant="subtitle1">Total: {suggestedCategories.total}</Typography>
            <StyledPagination
              current={Number(page) + 1 || 1}
              total={Math.ceil(suggestedCategories.total / Defaults.LIST_PAGE_SIZE)}
              onPageChange={(page: number) => setNewPage(page)}
            />
          </FullWidthBar>
        )}
      </Container>
      <Modal uniqueId="add-category-image" isOpen={modalIsOpen} handleClose={() => setModalIsOpen(false)}>
        <Formik
          initialValues={initialValues}
          validationSchema={ApproveCategorySchema}
          validateOnBlur={true}
          onSubmit={handleApproveCategory}
        >
          {({ errors, touched, setFieldValue, handleBlur }) => (
            <FormContainer sx={{ padding: '0 !important' }}>
              <Grid container>
                <Grid item sx={{ width: '100%' }}>
                  <BrowseUpload
                    error={touched.media && !!errors.media}
                    fileLabel="Upload Photo"
                    name="media"
                    handleFileInput={(event) => {
                      handlePhotoUpload(event, setFieldValue);
                    }}
                    handleBlur={handleBlur}
                    disabled={updateCategoryIsLoading || isUploading}
                    isUploading={isUploading}
                    errorMsg={photosErrorMsg}
                    helperText={errors.media && touched.media ? errors.media : null}
                  />
                </Grid>
                <Grid item sx={{ width: '100%' }}>
                  {photoMedia ? (
                    <>
                      <Typography>Uploaded Photo</Typography>
                      <img src={photoMedia?.smallUrl} width="100%" height="auto" alt="" />
                    </>
                  ) : null}
                </Grid>
              </Grid>
              <ButtonContainer>
                <Buttons>
                  <SubmitButton
                    type="submit"
                    disabled={updateCategoryIsLoading || isUploading}
                    loading={updateCategoryIsLoading || isUploading}
                    variant="contained"
                  >
                    Save
                  </SubmitButton>

                  <Button onClick={() => setModalIsOpen(false)} type="button" disabled={updateCategoryIsLoading || isUploading}>
                    Cancel
                  </Button>
                </Buttons>
              </ButtonContainer>
            </FormContainer>
          )}
        </Formik>
      </Modal>
    </>
  );
}

export default SuggestedCategoriesList;

const columns = [
  {
    text: 'Name',
    name: 'category.title',
  },
  {
    text: 'Username Requested',
    name: 'user.username',
  },
  {
    text: 'Date Requested',
    name: 'category.created',
  },
  {
    text: 'Actions',
    name: '',
  },
];

const Container = styled('div')({
  padding: '20px',
  backgroundColor: '#eee',
  flex: 1,
  display: 'flex',
  alignItems: 'stretch',
  flexDirection: 'column',
});

const FullWidthBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const StyledSearchField = styled(SearchField)({
  marginLeft: 'auto',
});

const PointerTableRow = styled(TableRow)({
  cursor: 'pointer',
});

const StyledTableRow = styled(PointerTableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledPagination = styled(Pagination)({
  alignSelf: 'flex-end',
});

const ButtonContainer = styled('div')({
  marginTop: '20px',
  alignSelf: 'stretch',
  flex: 1,
  display: 'flex',
  justifyContent: 'space-between',
});

const Buttons = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',

  '& > *': {
    margin: '5px',
  },
});
